import { createContext, useContext, useState, useEffect } from 'react';
import { api, useAuth } from './AuthContext';
import { useProject } from './ProjectContext';

const PackageGatesContext = createContext(null);

export const PackageGatesProvider = ({ children }) => {
  const { user } = useAuth();
  const { selectedProject } = useProject();
  const [gates, setGates] = useState({});
  const [order, setOrder] = useState(['basic', 'silver', 'gold', 'platinum']);

  useEffect(() => {
    api.get('/packages/gates')
      .then(r => {
        setGates(r.data.gates || {});
        setOrder(r.data.order || ['basic', 'silver', 'gold', 'platinum']);
      })
      .catch(() => {});
  }, []);

  // Project-level package takes precedence; fall back to user-level for cases where
  // the project was created after plan assignment (propagation gap) or admin set the
  // plan directly on the user record without going through the project endpoint.
  const currentPackage = selectedProject?.package_type || user?.package_type || null;

  // True when the selected project has a package tier assigned.
  // Use this to guard UI that should be hidden until the user selects a plan.
  const hasPackage = Boolean(currentPackage);

  // Returns true if `tier` meets or exceeds the minimum required for `feature`.
  // A null/undefined tier means no package is assigned — deny access to any gated feature.
  const canAccess = (feature, tier) => {
    const required = gates[feature];
    // No package assigned → deny everything (blur-gate and sidebar filter rely on this)
    if (!tier) return false;
    // Explicitly disabled by admin → deny regardless of tier
    if (required === 'off') return false;
    // Ungated feature → allow if the user at least has a package
    if (!required) return true;
    const tierIdx = order.indexOf(tier);
    const reqIdx = order.indexOf(required);
    if (tierIdx === -1 || reqIdx === -1) return true;
    return tierIdx >= reqIdx;
  };

  // canAccessCurrent: convenience wrapper using the active project's package_type.
  const canAccessCurrent = (feature) => canAccess(feature, currentPackage);

  return (
    <PackageGatesContext.Provider value={{ gates, order, canAccess, canAccessCurrent, currentPackage, hasPackage }}>
      {children}
    </PackageGatesContext.Provider>
  );
};

export const usePackageGates = () => {
  const context = useContext(PackageGatesContext);
  if (!context) {
    throw new Error('usePackageGates must be used within a PackageGatesProvider');
  }
  return context;
};
